import { useState, useRef } from 'react';
import clsx from 'clsx';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import useIsMobile from '@/hooks/use-is-mobile.hook';

interface FiltersTabsProps {
  tabs: { value: string; label: string }[];
  defaultValue: string;
  onChange: (value: string) => void;
  className?: string;
  tabClassName?: (isActive: boolean) => string;
}

export function FiltersTabs({ tabs, defaultValue, onChange, className, tabClassName }: FiltersTabsProps) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(defaultValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleTabClick = (value: string) => {
    setActiveTab(value);
    onChange(value);

    const modalContainer = document.querySelector(
      `[data-testid="${DataTestIdEnum.PROCEDURES_MODAL_CONTAINER}"]`,
    ) as HTMLElement | null;

    modalContainer?.scrollTo({ top: 0, behavior: 'smooth' });

    if (isMobile && containerRef.current) {
      const container = containerRef.current;
      const currentTab = document.getElementById(value);

      if (currentTab) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = currentTab.getBoundingClientRect();

        const left
          = elementRect.left
            - containerRect.left
            + container.scrollLeft;

        container.scrollTo({
          left: left - containerRect.width / 2 + elementRect.width / 2,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (containerRef.current?.offsetLeft || 0);
    scrollLeft.current = containerRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) {
      return;
    }
    e.preventDefault();
    const x = e.pageX - (containerRef.current?.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  return (
    <div className={clsx('w-full', className)}>
      <div className="relative w-full">
        <div
          ref={containerRef}
          className="mx-0 flex overflow-x-auto pl-5 gap-2 no-scrollbar cursor-grab scroll-smooth"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {tabs.map((tab) => (
            <button
              id={tab.value}
              data-testid={DataTestIdEnum.PROCEDURES_MODAL_FILTER_OPTIONS}
              key={tab.value}
              onClick={() => handleTabClick(tab.value)}
              className={clsx(
                'py-1 px-2 rounded whitespace-nowrap border text-md',
                activeTab === tab.value
                  ? 'border-success-400 bg-success-100 text-success-700'
                  : 'border-gray-300 bg-gray-100 text-gray-600',
                tabClassName?.(activeTab === tab.value),
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
